import React, {useRef} from 'react'
import {ButtonAction} from "../model/ButtonAction"
import {Icon, IconName} from "./Icon"
import {ItemAction} from '@dv5/domain'
import {useHoverMenu} from '../hooks/useHoverMenu'
import {useLongPress} from '../hooks/useLongPress'
import './ExtAddButton.less'

interface ExtAddButtonProps {
    actions: Array<ButtonAction>
    onAdd: (actionType: ItemAction) => void
    tooltipText?: string
    className?: string
    onShowPopup?: (isVisible: boolean) => void
    iconColor?: string
}

export const ExtAddButton: React.FC<ExtAddButtonProps> = ({
                                                                actions,
                                                                onAdd,
                                                                tooltipText = 'Add',
                                                                className = '',
                                                                onShowPopup,
                                                                iconColor,
                                                            }) => {
    const { showPopup, setShowPopup, onMouseEnter, onMouseLeave } = useHoverMenu({
        onShowPopup
    })
    const buttonRef = useRef<HTMLButtonElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)

    const handleShowPopup = (newShowPopup: boolean) => {
        setShowPopup(newShowPopup)
    }

    const { onPointerDown, onPointerUp, onPointerCancel, onPointerLeave, isLongPressTriggered } = useLongPress({
        onLongPress: () => handleShowPopup(true)
    })

    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        // Right click - show popup menu
        if (event.button === 2) {
            event.preventDefault()
            event.stopPropagation()
            handleShowPopup(true)
        }
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()

        // Left click - call onAdd with 0, unless a long press already opened the popup
        if (event.button === 0 && actions.length > 0 && !isLongPressTriggered()) {
            onAdd(actions[0].action)
        }
    }

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const handleActionClick = (event: React.MouseEvent, index: number) => {
        event.preventDefault()
        event.stopPropagation()
        handleShowPopup(false)
        if (index < actions.length) {
            onAdd(actions[index].action)
        }
    }

    // Close popup when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node) && !(buttonRef.current?.contains(event.target as Node) ?? false)) {
                handleShowPopup(false)
            }
        }

        if (showPopup) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showPopup])

    return (
        <div className={`ext-add-button-container ${className}`}>
            <button
                ref={buttonRef}
                className="ext-add-button"
                style={iconColor ? {color: iconColor} : undefined}
                onMouseDown={handleMouseDown}
                onClick={handleClick}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onPointerLeave={onPointerLeave}
                onContextMenu={handleContextMenu}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                aria-label="Add"
                title={`${tooltipText} (right click or long press for menu)`}
            >
                <Icon
                    name={actions.length === 0 ? IconName.Add : IconName.ExtAdd}
                    className="ext-add-icon"
                    width="20"
                    height="20"
                />
            </button>

            {showPopup && (
                <div ref={popupRef} className="ext-add-popup">
                    {actions.map((action, index) => (
                        <React.Fragment key={action.action}>
                            {index > 0 && action.separatorBefore && <hr className="ext-add-popup-separator"/>}
                            <div
                                className="ext-add-popup-item"
                                onMouseDown={event => handleActionClick(event, index)}
                            >
                                {action.text}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    )
}
